import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Mail, X, Search, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { api } from '../../lib/api';
import { Card, LoadingState, EmptyState, ErrorState } from '../../components/control-center/StateWrappers';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';

const STATUS_LABEL = {
  BORRADOR_CREADO: { label: 'Borrador creado', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  REVISION_HUMANA: { label: 'Revisión humana', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  NO_REQUIERE_RESPUESTA: { label: 'Sin respuesta', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
};
const StatusPill = ({ status }) => {
  const cfg = STATUS_LABEL[status] || { label: status || '—', cls: 'bg-slate-100 text-slate-700 border-slate-200' };
  return <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full border ${cfg.cls}`}>{cfg.label}</span>;
};

const fmtDate = (iso) => { try { return iso ? format(new Date(iso), 'dd/MM/yyyy HH:mm') : '—'; } catch { return '—'; } };
const val = (o, ...keys) => { for (const k of keys) if (o?.[k] !== undefined && o?.[k] !== null && o?.[k] !== '') return o[k]; return null; };

const PAGE_SIZE = 20;

const Emails = () => {
  const [state, setState] = useState({ loading: true, error: false, rows: [] });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [lang, setLang] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: false }));
    try {
      const { data } = await api.get('/control-center/logs');
      setState({ loading: false, error: false, rows: Array.isArray(data) ? data : [] });
      setPage(1);
    } catch {
      setState({ loading: false, error: true, rows: [] });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const languages = useMemo(() => {
    const s = new Set();
    state.rows.forEach((r) => { const l = val(r, 'idioma', 'language'); if (l) s.add(l); });
    return Array.from(s);
  }, [state.rows]);

  const statuses = useMemo(() => {
    const s = new Set();
    state.rows.forEach((r) => { const l = val(r, 'estado', 'status'); if (l) s.add(l); });
    return Array.from(s);
  }, [state.rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return state.rows.filter((r) => {
      const sender  = (val(r, 'remitente', 'sender', 'from') || '').toLowerCase();
      const subject = (val(r, 'asunto', 'subject') || '').toLowerCase();
      const st = val(r, 'estado', 'status') || '';
      const lg = val(r, 'idioma', 'language') || '';
      if (q && !sender.includes(q) && !subject.includes(q)) return false;
      if (status && st !== status) return false;
      if (lang && lg !== lang) return false;
      return true;
    });
  }, [state.rows, search, status, lang]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const rowsPage = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Correos</h1>
          <p className="text-slate-500 text-sm mt-1">Historial de correos procesados por la automatización.</p>
        </div>
        <Button onClick={load} className="bg-white text-slate-800 border border-slate-200 hover:bg-slate-50 gap-2">
          <RefreshCw className={`w-4 h-4 ${state.loading ? 'animate-spin' : ''}`} /> Actualizar
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e)=>{setSearch(e.target.value); setPage(1);}} placeholder="Buscar por remitente o asunto…" className="pl-9 border-2 border-slate-200 focus:border-blue-500" data-testid="emails-search" />
        </div>
        <select value={status} onChange={(e)=>{setStatus(e.target.value); setPage(1);}} className="border-2 border-slate-200 rounded-md h-10 px-3 text-sm focus:border-blue-500 focus:outline-none" data-testid="emails-status">
          <option value="">Todos los estados</option>
          {statuses.map((s)=>(<option key={s} value={s}>{STATUS_LABEL[s]?.label || s}</option>))}
        </select>
        <select value={lang} onChange={(e)=>{setLang(e.target.value); setPage(1);}} className="border-2 border-slate-200 rounded-md h-10 px-3 text-sm focus:border-blue-500 focus:outline-none" data-testid="emails-lang">
          <option value="">Todos los idiomas</option>
          {languages.map((l)=>(<option key={l} value={l}>{l}</option>))}
        </select>
      </Card>

      <Card className="overflow-hidden">
        {state.loading && <LoadingState />}
        {state.error && !state.loading && <ErrorState onRetry={load} />}
        {!state.loading && !state.error && filtered.length === 0 && (
          <EmptyState title="Sin correos" description="Aún no hay correos procesados o no coinciden con los filtros." icon={Mail} />
        )}
        {!state.loading && !state.error && filtered.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Fecha</th>
                    <th className="text-left px-4 py-3 font-semibold">Remitente</th>
                    <th className="text-left px-4 py-3 font-semibold">Asunto</th>
                    <th className="text-left px-4 py-3 font-semibold">Estado</th>
                    <th className="text-left px-4 py-3 font-semibold">Idioma</th>
                  </tr>
                </thead>
                <tbody>
                  {rowsPage.map((r, i)=>(
                    <tr key={i} onClick={()=>setSelected(r)} className="border-t border-slate-200 hover:bg-slate-50 cursor-pointer" data-testid={`email-row-${i}`}>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(val(r,'fecha','date','created_at'))}</td>
                      <td className="px-4 py-3 text-slate-900 font-medium truncate max-w-[220px]">{val(r,'remitente','sender','from') || '—'}</td>
                      <td className="px-4 py-3 text-slate-700 truncate max-w-[380px]">{val(r,'asunto','subject') || '—'}</td>
                      <td className="px-4 py-3"><StatusPill status={val(r,'estado','status')} /></td>
                      <td className="px-4 py-3 text-slate-600">{val(r,'idioma','language') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 text-sm text-slate-500">
              <span>{filtered.length} resultado{filtered.length===1?'':'s'}</span>
              <div className="flex items-center gap-2">
                <Button onClick={()=>setPage((p)=>Math.max(1,p-1))} disabled={page===1} className="h-8 px-3 bg-white text-slate-800 border border-slate-200 hover:bg-slate-50">Anterior</Button>
                <span>{page} / {totalPages}</span>
                <Button onClick={()=>setPage((p)=>Math.min(totalPages,p+1))} disabled={page===totalPages} className="h-8 px-3 bg-white text-slate-800 border border-slate-200 hover:bg-slate-50">Siguiente</Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Detail panel */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true">
          <div className="flex-1 bg-slate-900/40" onClick={()=>setSelected(null)} />
          <aside className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl" data-testid="email-detail">
            <div className="p-6 border-b border-slate-200 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Detalle</p>
                <h2 className="text-lg font-bold text-slate-900 truncate">{val(selected,'asunto','subject') || 'Correo'}</h2>
              </div>
              <button onClick={()=>setSelected(null)} className="p-2 rounded-lg hover:bg-slate-100" aria-label="Cerrar"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5 text-sm">
              <Field label="Fecha" value={fmtDate(val(selected,'fecha','date','created_at'))} />
              <Field label="Remitente" value={val(selected,'remitente','sender','from')} />
              <Field label="Idioma" value={val(selected,'idioma','language')} />
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Estado</p>
                <StatusPill status={val(selected,'estado','status')} />
              </div>
              <Block label="Mensaje del cliente" text={val(selected,'mensaje_cliente','original_message','customer_message','message')} />
              <Block label="Respuesta generada" text={val(selected,'respuesta','ai_response','draft_body','response')} />
              <div className="grid grid-cols-2 gap-4 pt-2 text-xs text-slate-500 border-t border-slate-200">
                <div><span className="block text-slate-400">Thread ID</span><span className="font-mono break-all">{val(selected,'thread_id','threadId') || '—'}</span></div>
                <div><span className="block text-slate-400">Draft ID</span><span className="font-mono break-all">{val(selected,'draft_id','draftId') || '—'}</span></div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{label}</p>
    <p className="text-slate-900 font-medium break-words">{value || '—'}</p>
  </div>
);
const Block = ({ label, text }) => (
  <div>
    <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">{label}</p>
    {text ? (
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-800 whitespace-pre-wrap text-sm leading-relaxed">{text}</div>
    ) : <p className="text-slate-500 text-sm">—</p>}
  </div>
);

export default Emails;
